/* ==========================================================================
   LSC - Social Feed behaviour
   Static, cacheable script for sections/lsc-social-feed.liquid.

   LSC - 2026-10-12 home audit: created. This is the script that used to ship
   inline at the bottom of the section (~17.4KB per home view, uncacheable),
   moved here unchanged in behaviour except for the audit fixes marked
   "LSC - 2026-10-12" below:
     - runs for EVERY instance of the section on the page (it used to bind to
       one hard-coded section id per inline copy);
     - re-initialises on the theme editor's shopify:section:load and tears
       down on shopify:section:unload, without stacking listeners;
     - one shared, requestAnimationFrame-throttled window resize listener
       (it used to add an unthrottled one per init);
     - a section-level Pause/Play toggle for the tile videos (WCAG 2.2.2).

   Vanilla, no dependencies. Everything degrades gracefully: with JS disabled
   the carousel is still a touch-scrollable strip, every post stays visible,
   no video is ever fetched, and the cover <img> still links to the post.

   MARKUP CONTRACT (all rendered by the section)
     .lsc-sf[data-lsc-sf]            one per section instance (the "root")
       [data-lsc-sf-video]           <video> with the mp4 in data-lsc-sf-src
       [data-lsc-sf-pause]           the Pause/Play toggle (ships [hidden])
       [data-lsc-sf-nav]             controls wrapper; [data-lsc-sf-pause-only]
                                     when it holds nothing but the toggle
       .lsc-sf__chip[data-filter]    account filter chips
       [data-lsc-sf-carousel]        present only for the carousel layout
       [data-lsc-sf-track]           the horizontally scrolling strip
       .lsc-sf__arrow[data-dir]      prev (-1) / next (1) buttons

   DEBUGGING
     Add ?lscdebug=1 to the URL and every instance narrates autoplay to the
     console. window.lscSf is the most recently initialised instance and
     window.lscSfAll the list of all live instances. Added after an evening in
     which every external check reported "healthy" while the feature did
     nothing - the section has to be able to explain itself.
   ========================================================================== */
(function () {
  'use strict';

  /* LSC - Load-once guard. The theme editor can re-insert this <script> tag
     when the section re-renders, which would evaluate this file again. A
     second evaluation must not add a second set of global listeners - it just
     asks the existing module to pick up any new, uninitialised instances. */
  if (window.LscSocialFeed) {
    window.LscSocialFeed.initAll(document);
    return;
  }

  var DEBUG = /[?&]lscdebug/.test(window.location.search);
  function log() {
    if (!DEBUG || !window.console) return;
    console.log.apply(console, ['[lsc-sf]'].concat([].slice.call(arguments)));
  }

  /* Enough to feel alive, few enough to stay cheap on a phone. Per instance. */
  var MAX_CONCURRENT = 4;

  var HAS_IO = 'IntersectionObserver' in window;
  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function prefersReduced() { return !!(reduceMotion && reduceMotion.matches); }

  /* LSC - 2026-10-12: every live instance. The few window/document-level
     listeners below are registered ONCE for the whole module and iterate this
     list, so re-initialising a section in the editor can never stack them. */
  var instances = [];

  /* ========================================================================
     Instance
     ======================================================================== */
  function init(root) {
    /* Already running for this exact element: nothing to do. A re-rendered
       section is a NEW element, so it initialises fresh. */
    if (root.lscSf) return root.lscSf;

    var inst = {
      root: root,
      videos: root.querySelectorAll('[data-lsc-sf-video]'),
      playing: [],
      /* LSC - 2026-10-12: tiles the play observer currently reports as
         intersecting, so "Play videos" can resume exactly the visible ones. */
      visible: [],
      /* LSC - 2026-10-12: true while the visitor has pressed "Pause videos".
         start() refuses while set, so neither observer nor the tab coming back
         into view can restart anything. */
      paused: false,
      io: null,
      loadIo: null,
      pauseBtn: root.querySelector('[data-lsc-sf-pause]'),
      nav: root.querySelector('[data-lsc-sf-nav]'),
      syncArrows: null
    };
    root.lscSf = inst;
    root.setAttribute('data-lsc-sf-init', '1');
    instances.push(inst);

    /* LSC - Video FIRST, then controls, each in its own try. This used to sit
       at the end, which meant any exception in the filter or carousel code
       silently took autoplay down with it, while the init flag still read
       '1' and made it look like the script had run. Features that do not
       depend on each other should not be able to break each other. */
    try { initVideo(inst); } catch (err) {
      if (window.console) console.error('[lsc-social-feed] video failed', err);
    }
    try { initControls(inst); } catch (err2) {
      /* Console only. A broken control is bad; a blank section is worse. */
      if (window.console) console.error('[lsc-social-feed] controls failed', err2);
    }

    if (DEBUG) {
      window.lscSf = inst;
      window.lscSfAll = instances;
      log('window.lscSf ready');
    }
    return inst;
  }

  function destroy(inst) {
    if (inst.io) inst.io.disconnect();
    if (inst.loadIo) inst.loadIo.disconnect();
    inst.playing.slice().forEach(function (v) { stop(inst, v); });
    var i = instances.indexOf(inst);
    if (i > -1) instances.splice(i, 1);
    try { delete inst.root.lscSf; } catch (e) { inst.root.lscSf = undefined; }
    inst.root.removeAttribute('data-lsc-sf-init');
    log('destroyed instance');
  }

  /* ========================================================================
     Video autoplay

     Twelve videos playing at once is a bad page - mobile data, battery and
     CLS all suffer - so tiles play only while visible, at most MAX_CONCURRENT
     at a time, never when the visitor has asked for reduced motion (who now
     download nothing at all, since hydrate() is what fetches and it refuses
     outright), and never while the visitor has pressed "Pause videos".
     ======================================================================== */

  function stop(inst, v) {
    /* The 'pause' event clears the playing marker. */
    try { v.pause(); } catch (e) {}
    var i = inst.playing.indexOf(v);
    if (i > -1) inst.playing.splice(i, 1);
  }

  /* LSC - Attach the media. Nothing before this point has fetched a byte of
     video, and that is the entire point.

     The element ships with no src and preload="none"; the URL waits in
     data-lsc-sf-src. Do not "simplify" this back into a <source> in the
     markup - with `autoplay` gone a <source> would still be fetched eagerly
     by preload, and with `autoplay` back it would be fetched regardless of
     preload. The markup is the one place the browser will always act on
     without asking us.

     preload is raised to "auto" in the same breath. With preload="none" left
     in place, assigning src fetches nothing and the load observer's head
     start would be a silent no-op. By the time we are this close to the
     viewport the whole file is wanted.

     Idempotent, because both observers call it and a second entry must not
     restart a download already in flight. */
  function hydrate(v) {
    if (v.dataset.lscSfHydrated) return;
    if (prefersReduced()) { log('hydrate blocked: reduced motion'); return; }
    var src = v.getAttribute('data-lsc-sf-src');
    if (!src) { log('hydrate skipped: no data-lsc-sf-src'); return; }
    v.dataset.lscSfHydrated = '1';
    v.preload = 'auto';
    v.src = src;
    try { v.load(); } catch (e) {}
    log('hydrated ->', src);
  }

  function start(inst, v) {
    if (prefersReduced()) { log('start blocked: reduced motion'); return; }
    /* LSC - 2026-10-12: the visitor's Pause wins over everything. */
    if (inst.paused) { log('start blocked: paused by visitor'); return; }
    if (inst.playing.indexOf(v) > -1) { log('start skipped: already playing'); return; }
    log('start ->', v.currentSrc || '(no src yet)');
    /* Oldest first, so scrolling hands play down the page. */
    while (inst.playing.length >= MAX_CONCURRENT) stop(inst, inst.playing[0]);
    inst.playing.push(v);
    /* LSC - Defensive, not redundant. The load observer normally gets here
       first, but a tile already on screen at page load can trip the play
       observer in the same frame, and visibilitychange / Play call start()
       directly. hydrate() is idempotent, so calling it twice is free. */
    hydrate(v);
    /* LSC - Deliberately NOT setting the playing marker here. It is owned by
       the 'playing' event. Setting it on intent rather than on fact is what
       let the play icon hide over a still frame when play() failed. */
    attempt(inst, v, 0);
  }

  /* LSC - play() rejects far more often than you would expect, and almost
     never permanently. Two AbortErrors seen on this very page:

       "The play() request was interrupted by a call to pause()"
         - our own stop(), fired by a callback that arrived while the play
           promise was still pending. Pure race, extremely common on scroll.

       "...because video-only background media was paused to save power"
         - Chrome pauses muted, audio-less media when it decides the page is
           not being looked at. Transient by definition.

     Treating any rejection as final guaranteed a tile never tried again,
     which is why tiles once sat on their cover still with everything else
     healthy. An AbortError on a tile that is still meant to be playing earns
     a retry; anything else is a real refusal and stops - a silent stop leaves
     the cover still showing, which is the correct fallback. */
  function attempt(inst, v, tries) {
    var p = v.play();
    if (!p || typeof p.then !== 'function') return;
    p.then(function () {
      log('play OK');
    }).catch(function (err) {
      var name = err && err.name;
      log('play REJECTED:', name, err && err.message, '| attempt', tries);
      if (name === 'AbortError' && inst.playing.indexOf(v) > -1 && tries < 3) {
        setTimeout(function () {
          if (inst.playing.indexOf(v) > -1 && !inst.paused) attempt(inst, v, tries + 1);
        }, 400);
        return;
      }
      stop(inst, v);
    });
  }

  function initVideo(inst) {
    var videos = inst.videos;
    log('videos found:', videos.length, '| IO supported:', HAS_IO);
    if (!videos.length || !HAS_IO) {
      updatePauseVisibility(inst);
      return;
    }

    /* LSC - Observe the TILE, never the <video>, and use isIntersecting ONLY,
       with threshold 0. Do not add a ratio test and do not raise the
       threshold - both have been tried and both break this section
       completely.

       The video is absolutely positioned inside a tile with overflow:hidden,
       inside a carousel with overflow-x:auto. In that stack the browser
       reports `isIntersecting: true` with `intersectionRatio: 0.000` (an
       empty intersection rect) while getBoundingClientRect shows a healthy
       239x425 box on screen, and ratios of 0.032 / 0.105 for tiles that fill
       the viewport. A ratio that never rises above zero can never cross ANY
       non-zero threshold, so {threshold: 0.6} fired never, {threshold: 0.25}
       fired never, and a `ratio >= 0.5` gate in the callback blocked every
       video. isIntersecting is the one signal that tracks reality here - false
       when a tile is scrolled out of the strip, true when it is not. The tile
       is an ordinary in-flow box and measures correctly. The cost is that a
       tile clipped to a sliver at the carousel edge may start; MAX_CONCURRENT
       bounds the damage. Correctness over cleverness. */
    inst.io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target.lscSfVideo;
        log('callback | intersecting:', entry.isIntersecting,
            '| ratio:', entry.intersectionRatio.toFixed(3),
            '| video attached:', !!v);
        if (!v) return;
        /* LSC - 2026-10-12: remember visibility even while paused, so Play
           can resume precisely the tiles that are on screen. */
        var at = inst.visible.indexOf(v);
        if (entry.isIntersecting) {
          if (at === -1) inst.visible.push(v);
          start(inst, v);
        } else {
          if (at > -1) inst.visible.splice(at, 1);
          stop(inst, v);
        }
      });
    }, { threshold: 0 });

    /* LSC - A SECOND observer, and it does exactly one thing: start the
       download a little early.

       Two observers rather than one with a rootMargin, because loading and
       playing want different moments. Loading wants a head start, so motion
       begins the instant a tile arrives instead of after a cold fetch.
       Playing wants the tile actually on screen - a rootMargin on the play
       observer would have tiles playing 300px outside the viewport, which is
       the battery drain this design exists to remove.

       Same rules as the play observer: threshold 0, isIntersecting only, and
       observe the TILE. It unobserves on first hydration - an element that no
       longer needs watching should not keep being watched.

       LSC - 2026-10-12: while the visitor has videos paused, nothing is
       downloaded ahead of time either (and the tile stays observed, so it can
       still pre-load after Play). Play itself hydrates whatever it starts. */
    inst.loadIo = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting || inst.paused) return;
        var v = entry.target.lscSfVideo;
        if (!v) return;
        log('load observer | hydrating ahead of view');
        hydrate(v);
        inst.loadIo.unobserve(entry.target);
      });
    }, { threshold: 0, rootMargin: '300px 0px' });

    /* LSC - There is NO HLS upgrade here, and there must not be one. It was
       tried: the playlist URL was passed as data and swapped in when
       canPlayType('application/vnd.apple.mpegurl') returned a non-empty
       string, on the theory that Safari answers honestly and Chrome does not.
       Chrome on macOS answers "maybe" and then renders nothing - videos played
       on Chrome/Windows and were blank on Chrome/macOS. That is the second
       time an HLS optimisation produced a video that reports success and
       shows nothing (after 'application/x-mpegURL' in the markup).
       canPlayType is not a reliable signal for HLS in Chrome under any
       spelling. mp4 everywhere. */
    Array.prototype.forEach.call(videos, function (v) {
      /* LSC - The playing marker is driven by the element's own media events,
         never by "we called play() so it must be playing". The CSS reflects
         what the video is actually doing, however it got there. */
      v.addEventListener('playing', function () {
        v.setAttribute('data-lsc-sf-playing', '1');
        /* LSC - Enrol on the EVENT, not on our own start() - a cap that
           counts intentions rather than actual playback describes nothing. */
        if (inst.playing.indexOf(v) === -1) inst.playing.push(v);
        while (inst.playing.length > MAX_CONCURRENT) {
          var oldest = inst.playing[0];
          if (oldest === v) break;
          log('over concurrency cap, pausing oldest');
          stop(inst, oldest);
        }
        /* LSC - 2026-10-12: a play that resolves after the visitor pressed
           Pause (the promise was already in flight) is stopped on arrival. */
        if (inst.paused) stop(inst, v);
        log('event: playing');
      });
      v.addEventListener('pause', function () {
        v.removeAttribute('data-lsc-sf-playing');
        log('event: pause');
      });

      var tile = v.closest('.lsc-sf__tile') || v;
      tile.lscSfVideo = v;
      inst.io.observe(tile);
      inst.loadIo.observe(tile);
      log('observing', tile === v ? 'VIDEO (no tile found!)' : 'tile');
    });

    updatePauseVisibility(inst);
  }

  /* ========================================================================
     LSC - 2026-10-12 home audit: Pause / Play toggle (WCAG 2.2.2).

     Looping autoplay video that lasts more than five seconds needs a way to
     stop it. One button per section instance, in the header controls.
       aria-pressed="false"  videos may play (observer-driven, as before)
       aria-pressed="true"   paused: every tile video is stopped and start()
                             refuses, so the observers, the tab returning to
                             the foreground and late play() promises cannot
                             restart them
     Pressing it again clears the flag and starts the tiles the play observer
     currently reports as on screen (still capped at MAX_CONCURRENT).

     The accessible name stays "Pause videos" (from the section's label
     setting) in both states, as the toggle-button pattern requires; the
     pressed state carries the state and the icon swaps for sighted users.
     ======================================================================== */
  function pauseUsable(inst) {
    return !!(inst.pauseBtn && inst.videos.length && HAS_IO && !prefersReduced());
  }

  /* Show the toggle only when it does something: this instance has video
     tiles, the observers exist, and reduced motion is off (reduced-motion
     visitors never get video at all). The root class lets the CSS size the
     header spacer that balances a centred heading. */
  function updatePauseVisibility(inst) {
    if (!inst.pauseBtn) return;
    var show = pauseUsable(inst);
    inst.pauseBtn.hidden = !show;
    if (inst.nav && inst.nav.hasAttribute('data-lsc-sf-pause-only')) inst.nav.hidden = !show;
    inst.root.classList.toggle('lsc-sf--has-pause', show);
  }

  function setPaused(inst, paused) {
    inst.paused = paused;
    if (inst.pauseBtn) inst.pauseBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
    if (paused) {
      /* Every video in the instance, not just the tracked ones, so nothing
         that slipped past the bookkeeping keeps running. */
      Array.prototype.forEach.call(inst.videos, function (v) { stop(inst, v); });
      log('paused by visitor');
    } else {
      inst.visible.slice().forEach(function (v) { start(inst, v); });
      log('resumed by visitor | visible tiles:', inst.visible.length);
    }
  }

  /* ========================================================================
     Controls: pause toggle, account filter, carousel arrows
     ======================================================================== */
  function initControls(inst) {
    var root = inst.root;

    if (inst.pauseBtn) {
      inst.pauseBtn.addEventListener('click', function () {
        setPaused(inst, !inst.paused);
      });
    }

    /* ---- Account filter ------------------------------------------------
       Purely client-side: every tile is already in the DOM, so filtering is
       a `hidden` toggle with no network round trip. */
    var chips = root.querySelectorAll('.lsc-sf__chip');
    var track = root.querySelector('[data-lsc-sf-track]');

    Array.prototype.forEach.call(chips, function (chip) {
      chip.addEventListener('click', function () {
        var filter = chip.dataset.filter;
        Array.prototype.forEach.call(chips, function (c) {
          c.setAttribute('aria-pressed', String(c === chip));
        });
        Array.prototype.forEach.call(root.querySelectorAll('.lsc-sf__tile'), function (tile) {
          tile.hidden = !(filter === 'all' || tile.dataset.account === filter);
        });
        /* Reset carousel scroll so the first visible tile is in view. */
        if (track) track.scrollTo({ left: 0, behavior: 'smooth' });
      });
    });

    /* ---- Carousel arrows -----------------------------------------------
       The arrows live in the header row, so they are not descendants of the
       carousel wrapper; search from the root. [data-lsc-sf-carousel] is
       still what says this instance is a carousel at all. Arrows are matched
       by [data-dir] as well, so the pause button can never be mistaken for
       one. */
    var wrap = root.querySelector('[data-lsc-sf-carousel]');
    if (!wrap || !track) return;
    var arrows = root.querySelectorAll('.lsc-sf__arrow[data-dir]');

    function pageWidth() {
      var tile = track.querySelector('.lsc-sf__tile:not([hidden])');
      if (!tile) return track.clientWidth;
      var gap = parseFloat(getComputedStyle(track).gap) || 0;
      /* Advance by a whole page of tiles, not a single tile. */
      var per = Math.max(1, Math.round(track.clientWidth / (tile.offsetWidth + gap)));
      return per * (tile.offsetWidth + gap);
    }

    function syncArrows() {
      var max = track.scrollWidth - track.clientWidth - 2;
      Array.prototype.forEach.call(arrows, function (a) {
        a.disabled = a.dataset.dir === '-1'
          ? track.scrollLeft <= 2
          : track.scrollLeft >= max;
      });
    }
    inst.syncArrows = syncArrows;

    Array.prototype.forEach.call(arrows, function (a) {
      a.addEventListener('click', function () {
        track.scrollBy({ left: pageWidth() * Number(a.dataset.dir), behavior: 'smooth' });
      });
    });

    /* The track belongs to this instance and is discarded with it, so this
       listener needs no teardown. Resize is handled by the single shared
       listener at the bottom of this file. */
    track.addEventListener('scroll', syncArrows, { passive: true });
    syncArrows();
  }

  /* ========================================================================
     Module-level listeners - registered exactly once
     ======================================================================== */

  /* LSC - 2026-10-12 home audit: ONE resize listener for every instance,
     throttled to one run per animation frame. The old inline script added an
     unthrottled `window.addEventListener('resize', syncArrows)` each time it
     ran, so it fired dozens of times per drag and stacked up across editor
     reloads. */
  var resizeQueued = false;
  window.addEventListener('resize', function () {
    if (resizeQueued) return;
    resizeQueued = true;
    window.requestAnimationFrame(function () {
      resizeQueued = false;
      instances.forEach(function (inst) {
        if (inst.syncArrows) inst.syncArrows();
      });
    });
  }, { passive: true });

  /* Nothing should keep playing in a tab nobody is looking at - and anything
     stopped for that reason must come back when the tab does (unless the
     visitor paused it), or a background/foreground round trip silently kills
     autoplay for the rest of the session. */
  document.addEventListener('visibilitychange', function () {
    instances.forEach(function (inst) {
      if (!inst.io) return;
      if (document.hidden) {
        inst.playing.slice().forEach(function (v) { stop(inst, v); });
        return;
      }
      Array.prototype.forEach.call(inst.videos, function (v) {
        var tile = v.closest('.lsc-sf__tile') || v;
        var r = tile.getBoundingClientRect();
        var onScreen = r.bottom > 0 && r.top < (window.innerHeight || 0) &&
                       r.right > 0 && r.left < (window.innerWidth || 0);
        if (onScreen) start(inst, v);
      });
    });
  });

  /* Honour the reduced-motion setting being changed after load, not just at
     load: stop everything and hide the pause toggle (and show it again if the
     preference is switched back off). */
  if (reduceMotion) {
    var onMotionChange = function (e) {
      instances.forEach(function (inst) {
        if (e.matches) inst.playing.slice().forEach(function (v) { stop(inst, v); });
        updatePauseVisibility(inst);
      });
    };
    if (typeof reduceMotion.addEventListener === 'function') reduceMotion.addEventListener('change', onMotionChange);
    else if (typeof reduceMotion.addListener === 'function') reduceMotion.addListener(onMotionChange);
  }

  /* ========================================================================
     Boot + theme editor lifecycle
     ======================================================================== */
  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-lsc-sf]');
    Array.prototype.forEach.call(roots, init);
    /* The scope itself may be the root (defensive; the section wrapper is
       normally the event target, with .lsc-sf inside it). */
    if (scope && scope.matches && scope.matches('[data-lsc-sf]')) init(scope);
  }

  function destroyWithin(scope) {
    instances.slice().forEach(function (inst) {
      if (scope === inst.root || (scope.contains && scope.contains(inst.root))) destroy(inst);
    });
  }

  /* Theme editor: a section re-render replaces its DOM. Tear down whatever
     was inside the old wrapper, then initialise the new one. Instances whose
     root has silently left the DOM are swept on every load as well, so a
     missed unload can never leave observers or list entries behind. */
  document.addEventListener('shopify:section:unload', function (e) {
    destroyWithin(e.target);
  });
  document.addEventListener('shopify:section:load', function (e) {
    instances.slice().forEach(function (inst) {
      if (!document.documentElement.contains(inst.root)) destroy(inst);
    });
    initAll(e.target);
  });

  window.LscSocialFeed = { initAll: initAll, instances: instances };

  /* `defer` means the document is parsed by now, but guard anyway in case the
     tag is ever loaded without it. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }
})();
